import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { MoreVertical, Users, Settings2, X, ArrowLeft, ArrowRight, UserPlus, UserMinus, Plus, Camera, Bot, Volume2, VolumeX } from 'lucide-react';
import { AI_BOT_NAME } from '@/lib/aiBot';
import { useCompanyId } from '@/hooks/useCompanyId';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { isRoomMuted, toggleRoomMuted } from '@/hooks/useUnreadCounts';

interface GroupMenuProps {
  room: { id: string; type: string; name: string | null; icon_url?: string | null; ai_bot_enabled?: boolean };
  onNameUpdated?: (newName: string) => void;
}

interface Member {
  userId: string;
  name: string;
  role: string;
}

export type GroupPanelView = 'none' | 'menu' | 'info' | 'settings' | 'addMembers';

interface AvailableUser {
  id: string;
  name: string;
  role: string;
}

/** Button only – rendered in the header */
export const GroupMenuButton = ({ onClick }: { onClick: () => void }) => (
  <button
    onClick={onClick}
    className="p-1.5 hover:bg-white/10 rounded-full transition-colors"
  >
    <MoreVertical className="h-5 w-5" />
  </button>
);

/** Full-height panel rendered inside the chat container */
export const GroupPanel = ({ room, view, onClose, onViewChange, onNameUpdated, onIconUpdated }: {
  room: GroupMenuProps['room'];
  view: GroupPanelView;
  onClose: () => void;
  onViewChange: (v: GroupPanelView) => void;
  onNameUpdated?: (newName: string) => void;
  onIconUpdated?: (newUrl: string) => void;
}) => {
  const { t, dir, lang } = useLanguage();
  const { user } = useAuth();
  const companyId = useCompanyId();
  const [members, setMembers] = useState<Member[]>([]);
  const [availableUsers, setAvailableUsers] = useState<AvailableUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [editName, setEditName] = useState(room.name || '');
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleTab, setRoleTab] = useState('all');
  const [iconUrl, setIconUrl] = useState(room.icon_url || '');
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [aiBotEnabled, setAiBotEnabled] = useState(room.ai_bot_enabled || false);
  const [roomMuted, setRoomMuted] = useState(() => isRoomMuted(room.id));

  const BackArrow = dir === 'rtl' ? ArrowRight : ArrowLeft;

  const fetchMembers = async () => {
    setLoading(true);
    const { data: memberRows } = await supabase
      .from('chat_room_members')
      .select('user_id')
      .eq('room_id', room.id);

    if (memberRows) {
      const userIds = memberRows.map(m => m.user_id);
      const [{ data: profiles }, { data: roles }] = await Promise.all([
        supabase.from('profiles_public' as any).select('id, name').in('id', userIds) as any,
        supabase.from('user_roles').select('user_id, role').in('user_id', userIds),
      ]);

      const roleMap: Record<string, string> = {};
      (roles || []).forEach(r => { roleMap[r.user_id] = r.role; });

      setMembers(
        (profiles || []).map(p => ({
          userId: p.id,
          name: p.name,
          role: roleMap[p.id] || 'employee',
        }))
      );
    }
    setLoading(false);
  };

  const fetchAvailableUsers = async () => {
    setLoading(true);
    const { data: memberRows } = await supabase
      .from('chat_room_members')
      .select('user_id')
      .eq('room_id', room.id);

    const existingIds = (memberRows || []).map(m => m.user_id);

    const { data: allProfiles } = await supabase.from('profiles_public' as any).select('id, name') as any;
    const nonMembers = (allProfiles || []).filter(p => !existingIds.includes(p.id));
    const userIds = nonMembers.map(p => p.id);

    if (userIds.length > 0) {
      const { data: roles } = await supabase.from('user_roles').select('user_id, role').in('user_id', userIds);
      const roleMap: Record<string, string> = {};
      (roles || []).forEach(r => { roleMap[r.user_id] = r.role; });

      setAvailableUsers(nonMembers.map(p => ({ id: p.id, name: p.name, role: roleMap[p.id] || 'employee' })));
    } else {
      setAvailableUsers([]);
    }
    setLoading(false);
  };

  const handleAddMember = async (userId: string) => {
    const { error } = await supabase.from('chat_room_members').insert({ room_id: room.id, user_id: userId });
    if (error) {
      toast.error(t('errorSavingUser'));
    } else {
      toast.success(t('statusUpdated'));
      setAvailableUsers(prev => prev.filter(u => u.id !== userId));
    }
  };

  const handleRemoveMember = async (userId: string) => {
    if (userId === user?.id) return; // can't remove yourself
    const { error } = await supabase.from('chat_room_members').delete().eq('room_id', room.id).eq('user_id', userId);
    if (error) {
      toast.error(t('errorSavingUser'));
    } else {
      toast.success(t('statusUpdated'));
      setMembers(prev => prev.filter(m => m.userId !== userId));
    }
  };

  const handleOpenInfo = () => {
    onViewChange('info');
    fetchMembers();
  };

  const handleOpenSettings = () => {
    onViewChange('settings');
    setEditName(room.name || '');
  };

  const handleSaveName = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    const { error } = await supabase
      .from('chat_rooms')
      .update({ name: editName.trim() })
      .eq('id', room.id);

    if (error) {
      toast.error(t('errorSavingUser'));
    } else {
      toast.success(t('statusUpdated'));
      onNameUpdated?.(editName.trim());
    }
    setSaving(false);
    onClose();
  };

  const handleIconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('يرجى اختيار صورة');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('حجم الصورة يجب أن لا يتجاوز 5MB');
      return;
    }
    setUploadingIcon(true);
    const ext = file.name.split('.').pop();
    const path = `${companyId || 'default'}/group-icons/${room.id}.${ext}`;
    const { error: uploadError } = await supabase.storage
      .from('chat-media')
      .upload(path, file, { upsert: true });
    if (uploadError) {
      toast.error(t('errorSavingUser'));
      setUploadingIcon(false);
      return;
    }
    // Store the path (not URL) so we can generate signed URLs on read
    const { error } = await supabase
      .from('chat_rooms')
      .update({ icon_url: path } as any)
      .eq('id', room.id);
    if (error) {
      toast.error(t('errorSavingUser'));
    } else {
      // Generate a signed URL for immediate display
      const { data: urlData } = await supabase.storage.from('chat-media').createSignedUrl(path, 3600);
      setIconUrl(urlData?.signedUrl || '');
      onIconUpdated?.(urlData?.signedUrl || '');
      toast.success(t('statusUpdated'));
    }
    setUploadingIcon(false);
  };

  const getAvatarColor = (name: string) => {
    const colors = ['#25D366', '#128C7E', '#075E54', '#34B7F1', '#00A884'];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return colors[Math.abs(hash) % colors.length];
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'driver': return t('driver');
      case 'customer': return t('customer');
      case 'admin': return 'مدير';
      default: return 'موظف';
    }
  };

  if (view === 'none') return null;

  return (
    <div className="absolute inset-0 z-10 flex flex-col bg-white">
      {/* Menu view */}
      {view === 'menu' && (
        <>
          <div className="flex items-center gap-3 px-3 py-2 bg-[#075E54] text-white shrink-0">
            <button onClick={onClose} className="p-1 hover:bg-white/10 rounded-full transition-colors">
              <BackArrow className="h-5 w-5" />
            </button>
            <h3 className="font-medium text-base">{t('settings')}</h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            <button
              onClick={handleOpenInfo}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-sm text-[#111b21] hover:bg-[#f0f0f0] transition-colors border-b border-[#e9e9e9]"
            >
              <Users className="h-5 w-5 text-[#667781]" />
              {t('groupInfo')}
            </button>
            <button
              onClick={handleOpenSettings}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-sm text-[#111b21] hover:bg-[#f0f0f0] transition-colors border-b border-[#e9e9e9]"
            >
              <Settings2 className="h-5 w-5 text-[#667781]" />
              {t('groupSettings')}
            </button>
          </div>
        </>
      )}

      {/* Info view */}
      {view === 'info' && (
        <>
          <div className="flex items-center gap-3 px-3 py-2 bg-[#075E54] text-white shrink-0">
            <button onClick={() => onViewChange('menu')} className="p-1 hover:bg-white/10 rounded-full transition-colors">
              <BackArrow className="h-5 w-5" />
            </button>
            <h3 className="font-medium text-base">{t('groupInfo')}</h3>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                {/* AI Bot toggle */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-[#e9e9e9]">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${aiBotEnabled ? 'bg-[#34B7F1]' : 'bg-[#667781]'}`}>
                    <Bot className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#111b21]">{AI_BOT_NAME}</p>
                    <p className="text-[11px] text-[#667781]">{aiBotEnabled ? (lang === 'ar' ? 'مفعّل' : 'Active') : (lang === 'ar' ? 'معطّل' : 'Inactive')}</p>
                  </div>
                  <button
                    onClick={async () => {
                      const newVal = !aiBotEnabled;
                      const { error } = await supabase.from('chat_rooms').update({ ai_bot_enabled: newVal } as any).eq('id', room.id);
                      if (!error) {
                        setAiBotEnabled(newVal);
                        toast.success(t('statusUpdated'));
                      } else {
                        toast.error(t('errorSavingUser'));
                      }
                    }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${aiBotEnabled ? 'bg-red-100 text-red-600 hover:bg-red-200' : 'bg-[#25D366] text-white hover:bg-[#128C7E]'}`}
                  >
                    {aiBotEnabled ? (lang === 'ar' ? 'إزالة' : 'Remove') : (lang === 'ar' ? 'إضافة' : 'Add')}
                  </button>
                </div>
                {/* Mute toggle */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-[#e9e9e9]">
                  <div className={`h-10 w-10 rounded-full flex items-center justify-center shrink-0 ${roomMuted ? 'bg-[#667781]' : 'bg-[#25D366]'}`}>
                    {roomMuted ? <VolumeX className="h-5 w-5 text-white" /> : <Volume2 className="h-5 w-5 text-white" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#111b21]">{lang === 'ar' ? 'صوت الإشعارات' : 'Notification Sound'}</p>
                    <p className="text-[11px] text-[#667781]">{roomMuted ? (lang === 'ar' ? 'مكتوم' : 'Muted') : (lang === 'ar' ? 'مفعّل' : 'Active')}</p>
                  </div>
                  <button
                    onClick={() => { const muted = toggleRoomMuted(room.id); setRoomMuted(muted); toast.success(muted ? (lang === 'ar' ? 'تم كتم الصوت' : 'Muted') : (lang === 'ar' ? 'تم تفعيل الصوت' : 'Unmuted')); }}
                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${roomMuted ? 'bg-[#25D366] text-white hover:bg-[#128C7E]' : 'bg-red-100 text-red-600 hover:bg-red-200'}`}
                  >
                    {roomMuted ? (lang === 'ar' ? 'تفعيل' : 'Unmute') : (lang === 'ar' ? 'كتم' : 'Mute')}
                  </button>
                </div>
                <button
                    onClick={() => { onViewChange('addMembers'); fetchAvailableUsers(); }}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-[#25D366] hover:bg-[#f0f0f0] transition-colors border-b border-[#e9e9e9]"
                  >
                    <div className="h-10 w-10 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
                      <UserPlus className="h-5 w-5 text-white" />
                    </div>
                    <span className="font-medium">{t('addMembers')}</span>
                  </button>
                <p className="px-4 py-2.5 text-xs text-[#667781] border-b border-[#e9e9e9]">
                  {members.length} عضو
                </p>
                {members.map(m => (
                  <div key={m.userId} className="flex items-center gap-3 px-4 py-2.5 border-b border-[#e9e9e9]">
                    <Avatar className="h-10 w-10 shrink-0">
                      <AvatarFallback style={{ backgroundColor: getAvatarColor(m.name) }} className="text-white text-sm font-medium">
                        {m.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#111b21] truncate">{m.name}</p>
                      <p className="text-[11px] text-[#667781]">{getRoleLabel(m.role)}</p>
                    </div>
                    {m.userId !== user?.id && (
                      <button
                        onClick={() => handleRemoveMember(m.userId)}
                        className="p-1.5 hover:bg-red-50 rounded-full transition-colors"
                        title="إزالة"
                      >
                        <UserMinus className="h-4 w-4 text-red-500" />
                      </button>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}

      {/* Settings view */}
      {view === 'settings' && (
        <>
          <div className="flex items-center gap-3 px-3 py-2 bg-[#075E54] text-white shrink-0">
            <button onClick={() => onViewChange('menu')} className="p-1 hover:bg-white/10 rounded-full transition-colors">
              <BackArrow className="h-5 w-5" />
            </button>
            <h3 className="font-medium text-base">{t('groupSettings')}</h3>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {/* Group Icon */}
            <div className="flex flex-col items-center gap-2">
              <label className="text-xs text-[#667781] mb-1 block">{t('groupIcon') || 'أيقونة المجموعة'}</label>
              <div className="relative cursor-pointer group" onClick={() => document.getElementById('icon-upload')?.click()}>
                {iconUrl ? (
                  <img src={iconUrl} alt="Group icon" className="h-20 w-20 rounded-full object-cover border-2 border-[#25D366]" />
                ) : (
                  <div className="h-20 w-20 rounded-full bg-[#25D366] flex items-center justify-center text-white text-2xl font-bold">
                    {(room.name || '?').charAt(0).toUpperCase()}
                  </div>
                )}
                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  {uploadingIcon ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera className="h-6 w-6 text-white" />
                  )}
                </div>
                <input
                  id="icon-upload"
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleIconUpload}
                  disabled={uploadingIcon}
                />
              </div>
            </div>
            <div>
              <label className="text-xs text-[#667781] mb-1.5 block">{t('groupName')}</label>
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                className="text-sm"
              />
            </div>
            <Button
              onClick={handleSaveName}
              disabled={saving || !editName.trim()}
              className="w-full bg-[#25D366] hover:bg-[#128C7E]"
            >
              {t('save')}
            </Button>
            <div className="pt-4 mt-auto space-y-2">
              <Button
                variant="destructive"
                onClick={async () => {
                  if (!user) return;
                  const confirmed = window.confirm(lang === 'ar' ? 'هل أنت متأكد من حذف هذه المجموعة؟ سيتم حذف جميع الرسائل.' : 'Are you sure you want to delete this group? All messages will be deleted.');
                  if (!confirmed) return;
                  // Delete all messages, members, then the room
                  await supabase.from('chat_messages').delete().eq('room_id', room.id);
                  await supabase.from('chat_room_members').delete().eq('room_id', room.id);
                  const { error } = await supabase.from('chat_rooms').delete().eq('id', room.id);
                  if (error) {
                    toast.error(t('errorSavingUser'));
                  } else {
                    toast.success(t('statusUpdated'));
                    onClose();
                    window.dispatchEvent(new CustomEvent('left-group'));
                  }
                }}
                className="w-full"
              >
                {lang === 'ar' ? 'حذف المجموعة' : 'Delete Group'}
              </Button>
              <Button
                variant="outline"
                onClick={async () => {
                  if (!user) return;
                  const { error } = await supabase
                    .from('chat_room_members')
                    .delete()
                    .eq('room_id', room.id)
                    .eq('user_id', user.id);
                  if (error) {
                    toast.error(t('errorSavingUser'));
                  } else {
                    toast.success(t('statusUpdated'));
                    onClose();
                    window.dispatchEvent(new CustomEvent('left-group'));
                  }
                }}
                className="w-full border-red-300 text-red-600 hover:bg-red-50"
              >
                {t('leaveGroup') || 'مغادرة المجموعة'}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Add Members view */}
      {view === 'addMembers' && (() => {
        const filtered = availableUsers
          .filter(u => roleTab === 'all' || u.role === roleTab)
          .filter(u => !searchQuery || u.name.toLowerCase().includes(searchQuery.toLowerCase()));
        const tabs = [
          { key: 'all', label: lang === 'ar' ? 'الكل' : 'All' },
          { key: 'employee', label: lang === 'ar' ? 'موظفين' : 'Employees' },
          { key: 'driver', label: lang === 'ar' ? 'سائقين' : 'Drivers' },
          { key: 'customer', label: lang === 'ar' ? 'عملاء' : 'Customers' },
        ];
        return (
        <>
          <div className="flex items-center gap-3 px-3 py-2 bg-[#075E54] text-white shrink-0">
            <button onClick={() => { onViewChange('info'); fetchMembers(); }} className="p-1 hover:bg-white/10 rounded-full transition-colors">
              <BackArrow className="h-5 w-5" />
            </button>
            <h3 className="font-medium text-base">{t('addMembers')}</h3>
          </div>
          {/* Role Tabs */}
          <div className="flex border-b border-[#e9e9e9] shrink-0">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setRoleTab(tab.key)}
                className={`flex-1 py-2 text-xs font-medium transition-colors ${roleTab === tab.key ? 'text-[#25D366] border-b-2 border-[#25D366]' : 'text-[#667781] hover:text-[#111b21]'}`}
              >
                {tab.label}
                <span className="ml-1 text-[10px]">
                  ({availableUsers.filter(u => tab.key === 'all' || u.role === tab.key).length})
                </span>
              </button>
            ))}
          </div>
          <div className="px-3 py-2 shrink-0">
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('search')}
              className="text-sm"
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="w-6 h-6 border-2 border-[#25D366] border-t-transparent rounded-full animate-spin" />
              </div>
            ) : filtered.length === 0 ? (
              <p className="text-center text-sm text-[#667781] py-8">{lang === 'ar' ? 'لا توجد جهات اتصال متاحة' : 'No contacts available'}</p>
            ) : (
              filtered.map(u => (
                  <div key={u.id} className="flex items-center gap-3 px-4 py-2.5 border-b border-[#e9e9e9]">
                    <Avatar className="h-10 w-10 shrink-0">
                      <AvatarFallback style={{ backgroundColor: getAvatarColor(u.name) }} className="text-white text-sm font-medium">
                        {u.name.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[#111b21] truncate">{u.name}</p>
                      <p className="text-[11px] text-[#667781]">{getRoleLabel(u.role)}</p>
                    </div>
                    <button
                      onClick={() => handleAddMember(u.id)}
                      className="p-1.5 hover:bg-green-50 rounded-full transition-colors"
                    >
                      <Plus className="h-5 w-5 text-[#25D366]" />
                    </button>
                  </div>
                ))
            )}
          </div>
        </>
        );
      })()}
    </div>
  );
};

export default GroupPanel;
